///
/// EarthBrowser
///
/// Created by Matt Giger on September 23, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Weekly signup-cohort retention from the tenure columns: cohort size,
/// share still active in the past 7 days, verification rate, and average
/// distinct active days. First product-health surface in the console —
/// deliberately simple until per-week activity history exists.
///

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useGetRetentionQuery } from '@/redux/api/userInsightsApi'

function pct(part: number, whole: number): string {
  if (whole === 0) return '—'
  return `${Math.round((part / whole) * 100)}%`
}

function RetentionBar({ part, whole }: { part: number; whole: number }) {
  const fraction = whole > 0 ? part / whole : 0
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-blue-500 transition-all duration-300"
          style={{ width: `${Math.round(fraction * 100)}%` }}
        />
      </div>
      <span className="text-xs w-10 text-right">{pct(part, whole)}</span>
    </div>
  )
}

export function RetentionSection() {
  const { data, isLoading } = useGetRetentionQuery()

  if (isLoading) {
    return <Skeleton className="h-48 rounded-xl" />
  }

  const cohorts = data?.cohorts ?? []
  if (cohorts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">No signups in the last 12 weeks</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Weekly Cohorts</h3>
        <p className="text-xs text-muted-foreground">
          Users who signed up each week, and how many of them were active in the last 7 days
        </p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">Week of</TableHead>
            <TableHead className="text-xs text-right">Signups</TableHead>
            <TableHead className="text-xs text-right">Verified</TableHead>
            <TableHead className="text-xs w-[40%]">Active last 7d</TableHead>
            <TableHead className="text-xs text-right">Avg active days</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cohorts.map(cohort => (
            <TableRow key={cohort.week}>
              <TableCell className="text-xs font-medium">
                {new Date(cohort.week).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </TableCell>
              <TableCell className="text-xs text-right">{cohort.cohort_size}</TableCell>
              <TableCell className="text-xs text-right text-muted-foreground">
                {pct(cohort.verified, cohort.cohort_size)}
              </TableCell>
              <TableCell><RetentionBar part={cohort.active_7d} whole={cohort.cohort_size} /></TableCell>
              <TableCell className="text-xs text-right">{cohort.avg_active_days}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
